import { io, Socket } from 'socket.io-client';
import { supervisionService, employeeService, patientService } from './api';

export interface ConnectionState {
  isConnected: boolean;
  isReconnecting: boolean;
  lastUpdated: Date | null;
  connectionType: 'socket' | 'polling' | 'offline';
}

export interface SupervisionUpdate {
  type: 'STATUS_CHANGE' | 'NEW_ASSIGNMENT' | 'COMPLETION' | 'OVERDUE_ALERT';
  supervisionId: string;
  data: any;
  timestamp: Date;
}

export interface DashboardData {
  supervisions: any[];
  employees: any[];
  patients: any[];
  lastUpdated: Date;
}

export class SupervisionSocketManager {
  private socket: Socket | null = null;
  private pollingInterval: NodeJS.Timeout | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private listeners: Map<string, Function[]> = new Map();
  private connectionState: ConnectionState = {
    isConnected: false,
    isReconnecting: false,
    lastUpdated: null,
    connectionType: 'offline'
  };
  private pollingIntervalMs = 60000; // 60 seconds (reduced from 30s to minimize API requests)
  private maxReconnectAttempts = 5;
  private reconnectAttempts = 0;
  private lastDataHash: string | null = null;

  constructor(private baseUrl?: string) {
    // Don't start connection automatically - wait for explicit start() call
    console.log('SupervisionSocketManager created, waiting for start() call');
  }

  /**
   * Start the supervision socket manager connection (should be called after authentication)
   */
  public async start(): Promise<void> {
    if (this.connectionState.isConnected) {
      console.log('SupervisionSocketManager already started');
      return;
    }
    console.log('Starting SupervisionSocketManager...');
    await this.initializeConnection();
  }

  private async initializeConnection(): Promise<void> {
    // Check if Socket.io server is available by attempting to connect
    if (this.baseUrl) {
      try {
        await this.initializeSocketConnection();
      } catch (error) {
        console.log('Socket.io not available, falling back to polling', error);
        this.initializePolling();
      }
    } else {
      // No baseUrl provided, use polling
      this.initializePolling();
    }
  }

  private async initializeSocketConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.socket = io(this.baseUrl!, {
        timeout: 5000,
        transports: ['websocket', 'polling']
      });

      const timeout = setTimeout(() => {
        this.socket?.disconnect();
        reject(new Error('Socket.io connection timeout'));
      }, 5000);

      this.socket.on('connect', () => {
        clearTimeout(timeout);
        this.connectionState = {
          isConnected: true,
          isReconnecting: false,
          lastUpdated: new Date(),
          connectionType: 'socket'
        };
        this.reconnectAttempts = 0;
        this.emit('connection_state_change', this.connectionState);
        this.setupSocketListeners();
        resolve();
      });

      this.socket.on('connect_error', (error) => {
        clearTimeout(timeout);
        reject(error);
      });
    });
  }

  private setupSocketListeners(): void {
    if (!this.socket) return;

    this.socket.on('supervision_update', (update: SupervisionUpdate) => {
      this.emit('supervision_update', update);
    });

    this.socket.on('dashboard_data', (data: DashboardData) => {
      this.connectionState.lastUpdated = new Date();
      this.emit('dashboard_data', data);
      this.emit('connection_state_change', this.connectionState);
    });

    this.socket.on('disconnect', () => {
      this.connectionState.isConnected = false;
      this.connectionState.connectionType = 'offline';
      this.emit('connection_state_change', this.connectionState);
      this.handleReconnection();
    });

    this.socket.on('reconnect', () => {
      this.connectionState.isConnected = true;
      this.connectionState.isReconnecting = false;
      this.connectionState.connectionType = 'socket';
      this.reconnectAttempts = 0;
      this.emit('connection_state_change', this.connectionState);
    });
  }

  private initializePolling(): void {
    this.connectionState = {
      isConnected: true,
      isReconnecting: false,
      lastUpdated: null,
      connectionType: 'polling'
    };
    this.emit('connection_state_change', this.connectionState);
    this.startPolling();
  }

  private startPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }

    this.pollingInterval = setInterval(async () => {
      try {
        await this.fetchDashboardData();
      } catch (error) {
        console.error('Polling error:', error);
        this.handlePollingError();
      }
    }, this.pollingIntervalMs);

    // Initial data fetch
    this.fetchDashboardData().catch(error => {
      console.error('Initial data fetch error:', error);
      this.handlePollingError();
    });
  }

  private async fetchDashboardData(): Promise<void> {
    try {
      const [supervisions, employeesResponse, patientsResponse] = await Promise.all([
        supervisionService.getSupervisions(),
        employeeService.getAllEmployees(),
        patientService.getAllPatients()
      ]);

      const employees = employeesResponse;
      const patients = patientsResponse;

      const data: DashboardData = {
        supervisions,
        employees,
        patients,
        lastUpdated: new Date()
      };

      // Check if data has changed using a simple hash
      const dataHash = JSON.stringify({ 
        supervisionCount: supervisions.length,
        employeeCount: employees.length,
        patientCount: patients.length,
        supervisionStatuses: supervisions.map((s: any) => ({ id: s.id, status: s.completionStatus }))
      });

      if (this.lastDataHash !== dataHash) {
        this.lastDataHash = dataHash;
        this.connectionState.lastUpdated = new Date();
        this.emit('dashboard_data', data);
      }

      this.connectionState.isConnected = true;
      this.connectionState.isReconnecting = false;
      this.reconnectAttempts = 0;
      this.emit('connection_state_change', this.connectionState);

    } catch (error) {
      throw error;
    }
  }

  private handlePollingError(): void {
    this.reconnectAttempts++;
    
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.connectionState.isConnected = false;
      this.connectionState.connectionType = 'offline';
      this.emit('connection_state_change', this.connectionState);
      return;
    }

    this.connectionState.isReconnecting = true;
    this.emit('connection_state_change', this.connectionState);

    // Exponential backoff
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectTimeout = setTimeout(() => {
      this.fetchDashboardData().catch(() => {
        this.handlePollingError();
      });
    }, delay);
  }

  private handleReconnection(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      // Fallback to polling
      this.initializePolling();
      return;
    }

    this.connectionState.isReconnecting = true;
    this.emit('connection_state_change', this.connectionState);
    this.reconnectAttempts++;
  }

  public on(event: string, callback: Function): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)?.push(callback);
  }

  public off(event: string, callback?: Function): void {
    if (!callback) {
      this.listeners.delete(event);
      return;
    }

    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      const index = eventListeners.indexOf(callback);
      if (index > -1) {
        eventListeners.splice(index, 1);
      }
    }
  }

  private emit(event: string, data?: any): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  public getConnectionState(): ConnectionState {
    return { ...this.connectionState };
  }

  public forceRefresh(): Promise<void> {
    if (this.connectionState.connectionType === 'socket' && this.socket?.connected) {
      this.socket.emit('request_dashboard_data');
      return Promise.resolve();
    } else if (this.connectionState.connectionType === 'polling') {
      return this.fetchDashboardData();
    }
    return Promise.reject(new Error('No active connection'));
  }

  public setPollingInterval(intervalMs: number): void {
    this.pollingIntervalMs = intervalMs;
    if (this.connectionState.connectionType === 'polling' && this.pollingInterval) {
      this.startPolling();
    }
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.connectionState = {
      isConnected: false,
      isReconnecting: false,
      lastUpdated: null,
      connectionType: 'offline'
    };

    this.listeners.clear();
  }

  public isConnected(): boolean {
    return this.connectionState.isConnected;
  }
}

// Singleton instance
export const supervisionSocketManager = new SupervisionSocketManager();
